import { useCallback } from 'react';
import { trpc } from '@client-trpc';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import { useWindow } from '@client/window/window.hook';
import type { WorkspaceLayoutSnapshot } from '@server/trading-layouts/schemas';
import type { SaveTradingLayoutFormState } from '@client/features/trading-layouts/save-trading-layout-form';

export const useCreateWorkspaceLayout = () => {
  const { notify } = useNotifications();
  const { closeWindow } = useWindow();
  const utils = trpc.useUtils();
  const { mutateAsync: createWorkspaceLayout } =
    trpc.workspaceLayouts.createWorkspaceLayout.useMutation();
  const { mutateAsync: updateWorkspaceLayout } =
    trpc.workspaceLayouts.updateWorkspaceLayout.useMutation();

  const onCreateSubmit = useCallback(
    (snapshot: WorkspaceLayoutSnapshot) =>
      async (formState: SaveTradingLayoutFormState) => {
        try {
          await createWorkspaceLayout({
            name: formState.name,
            notes: formState.notes?.trim() || undefined,
            snapshot
          });

          await utils.workspaceLayouts.workspaceLayouts.invalidate();

          notify({
            type: 'success',
            message: 'Workspace layout saved successfully'
          });

          closeWindow('SaveTradingLayoutForm');
        } catch (error) {
          notify({
            type: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Failed to save workspace layout'
          });
        }
      },
    [closeWindow, createWorkspaceLayout, notify, utils]
  );

  const onUpdateSubmit = useCallback(
    (layoutId: string, snapshot: WorkspaceLayoutSnapshot) =>
      async (formState: SaveTradingLayoutFormState) => {
        try {
          await updateWorkspaceLayout({
            layoutId,
            name: formState.name,
            notes: formState.notes?.trim() || undefined,
            snapshot
          });

          await utils.workspaceLayouts.workspaceLayouts.invalidate();

          notify({
            type: 'success',
            message: 'Workspace layout updated successfully'
          });

          closeWindow('SaveTradingLayoutForm');
        } catch (error) {
          notify({
            type: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Failed to update workspace layout'
          });
        }
      },
    [closeWindow, notify, updateWorkspaceLayout, utils]
  );

  return {
    onCreateSubmit,
    onUpdateSubmit
  };
};
